import { useQuery } from "@tanstack/react-query";
import styles from './styles.module.css';
import StaggeredList, { IStaggeredListItem } from "../StaggeredList";
import { MutableRefObject, useMemo, useState, useRef, useEffect } from "react";
import { IUploadPopUpRef } from "../UploadPopUp";
import { segmentEventPayload,homepageFeatureAtom, presetItemAtom, entitySwitchedAtom } from "@/modules/editor-v2/Atoms";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import { useRouter } from "next/router";
import { getHomePageMarketPlaces } from "@/modules/editor-v2/services/HomePage";
import { useSelector } from "react-redux";
import { TrackWebappEvent } from "src/modules/segment-event-tracking/segmentEventTracking";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import { Skeleton } from "@mui/material";
import GoogleDriveUploadPopUp from '../GoogleDrivePopUp';
import { EditorScreen } from "@/modules/batchEditor/BulkUploadEditor/constant";
import useToast from "@/hooks/useToast";
import { aiTaskDisableAtom } from "@/modules/batchEditor/atoms";
import { css } from "@emotion/react";

interface IMarketPlacesProps {
    popUpRef:MutableRefObject<IUploadPopUpRef>,
    userId:String,
    companyName:String,
}

export default function CustomMarketplaces({popUpRef, userId, companyName}: IMarketPlacesProps) {
    const [switchedEntity, setSwitchedEntity] = useAtom(entitySwitchedAtom);
    const {data, isLoading:marketPlaceLoading, isError, refetch} = useQuery(
        ["marketplaces", userId],
        getHomePageMarketPlaces
    );
    const timerRef = useRef(null);
    const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
    const router = useRouter();
    const setHomepageFeature = useSetAtom(homepageFeatureAtom);
    const strapiData:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.StaggeredListItemType?.StaggeredListItemType);
    const marketPlacesStrapiData:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.StaggeredListItemType?.StaggeredListItemType?.ListItemType?.filter((item)=> item.listTypeName === "Marketplaces"));
    const googleDrivePopUpRef = useRef<any>();
    const [marketplaceId, setMarketplaceId] = useState('');
    const [aiTaskDisable, setAiTaskDisable] = useAtom(aiTaskDisableAtom);
    const toaster = useToast();
    const sliderRef = useRef<HTMLDivElement>();
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [presetItem, setPresetItem] = useAtom<any>(presetItemAtom);

    const items:IStaggeredListItem[] = useMemo(()=>data?.map(item=>({
            title:item?.name, 
            image:item?.logo,
            widgetImage: marketPlacesStrapiData?.find((marketplace)=> marketplace.title.toLowerCase() === item?.name.toLowerCase())?.widgetImage || "https://dresma-assets.s3.us-east-2.amazonaws.com/DoMyShoot/App+Screens/501.png",
            extraData: {marketplaceId: item?.marketplace_id, size: item?.size},
            aiTaskDisable: item?.aiTaskDisable,
            type: item?.type
        })
    ).filter(item => item?.type === 'Custom'),[data]);

    const calcArrows = () => {
        if(timerRef.current)clearTimeout(timerRef.current);
        timerRef.current = setTimeout(()=>{
        const sliderWidth = sliderRef.current?.scrollWidth;
        const slidesWidth = sliderRef.current?.clientWidth;
        const scrollXOffset = sliderRef.current?.scrollLeft;
        setShowRightArrow(sliderWidth > scrollXOffset + slidesWidth + 10);
        setShowLeftArrow(!!scrollXOffset);
        },300);
    }

    useEffect(()=>{
        if(!sliderRef.current) return;
        const observer = new ResizeObserver(calcArrows);
        sliderRef.current.addEventListener("scroll",calcArrows);
        observer.observe(sliderRef.current);
        return () => {
            sliderRef.current?.removeEventListener("scroll",calcArrows);
            observer.disconnect();
        }
    },[items?.length]);

    const onUploadSuccessGenerator = (item) => async ({batchId="", initiatedSource, jobId=""})=>{
        if (item == undefined) item = presetItem;
        if ( item?.aiTaskDisable || item?.aiTaskDisable) {
            setAiTaskDisable(true);
            router.push(`/dashboard/batch-editor?mode=${EditorScreen.PRODUCT_LIFESTYLE}&batchAI=${batchId}`);
            sessionStorage.removeItem('videoUploadOnAiTaskDisable');
            return;
        } else if (sessionStorage.getItem('videoUploadOnAiTaskDisable') && !item?.aiTaskDisable) {
            toaster.error("Cannot upload Video Formats on Selected Preset");
            sessionStorage.removeItem('videoUploadOnAiTaskDisable');
            setPresetItem(null);
            return;
        }
        if(!item?.title) {
            const presetDetails = {
                size: {
                    height: 1500,
                    width: 1500
                },
                coverage: 80,
                position: "centre",
                backgroundColor: "#FFFFFF",
            }
            jobId ? router.push(`/dashboard/batch-editor/${jobId}?marketplaceSource=${"Custom"}&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}&initiatedSource=marketplace`) 
            : router.push(`/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&marketplaceSource=${"Custom"}&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}&initiatedSource=marketplace`);
            popUpRef?.current?.close();
            return;
        }
       
        jobId ? router.push(`/dashboard/batch-editor/${jobId}?marketplaceSource=${item?.title}&marketplaceId=${item?.extraData?.marketplaceId}`) 
        : router.push(
          `/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&marketplaceSource=${item?.title}&marketplaceId=${item?.extraData?.marketplaceId}&initiatedSource=marketplace`
        );
        popUpRef?.current?.close();
        return;
    }

    const onItemClick = (item) => {
        setMarketplaceId(item?.extraData?.marketplaceId)
        setHomepageFeature(item?.title);
        const eventPayload = {
            homepageFeature: item?.title,
            ...getSegmentPayloadAtom
          };
        TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_marketplace_hp}`, eventPayload);
        popUpRef.current.open({
            title: item?.title || "Custom",
            onUploadSuccess: onUploadSuccessGenerator(item),
            widgetImage: item?.widgetImage,
            batchEditorJobPopUp: true,
            marketplaceId: item?.extraData?.marketplaceId,
            aiTaskDisable: item?.aiTaskDisable,
            onGoogleDriveUpload: onGoogleDriveUploadGenerator(item)
        })
    }

    const onGoogleDriveUploadGenerator = (item) => async ()  =>{
        setPresetItem(item);
        googleDrivePopUpRef?.current?.open(
            {
                onUploadSuccess: onUploadSuccessGenerator(item),
            });
        window.setTimeout(()=>popUpRef.current.close(),100);
    }

    useEffect(()=>{
        if(switchedEntity)refetch();
    },[switchedEntity]);
    
    return <div css={emotionStyles.parentContainer}>
        {
            
            marketPlaceLoading ? (
                    <div css={emotionStyles.skeletonContainer}>
                        {Array(4).fill(null).map(item => (<Skeleton animation={'wave'} variant={"rectangular"} width={170} height={130} style={{marginRight:6, borderRadius:8, backgroundColor:'#EEEEEE'}} />))}
                    </div>
                )
                : (
                    <div css={emotionStyles.container(items?.length>0)}>
                        <div className={styles.heading}>
                            {items?.length>0 && `${strapiData?.CreateFor || 'Create for'} ${companyName}`}
                        </div>
                        <div>
                        {showLeftArrow && <div css={emotionStyles.leftArrow} onClick={()=>{
                                sliderRef.current.scrollBy({
                                    left: -600,
                                    behavior: 'smooth'
                                })
                            }} >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M8 4L16 12L8 20" stroke="#262626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>}
                        <div ref={sliderRef} css={emotionStyles.containerBox} >
                            <StaggeredList items={items} backgroundType="cover" onItemClick={onItemClick} />
                            <GoogleDriveUploadPopUp ref={googleDrivePopUpRef} onUploadSuccess={onUploadSuccessGenerator()} onBackPress={()=>{}} marketPlaceIntiatedSource={marketplaceId} initiatedSource={'marketplace'}/>       
                        </div>
                        {showRightArrow && <div css={emotionStyles.rightArrow} onClick={()=>{
                                sliderRef.current.scrollBy({
                                    left: 600,
                                    behavior: 'smooth'
                                })
                            }} >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M8 4L16 12L8 20" stroke="#262626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>}
                        </div>
                    </div>
                )
        }
    </div>
}

const emotionStyles = {
    parentContainer: css`
        display: flex;
        flex-direction: column;
        gap:16px;
    `,
    container: (flag) => css`
        position:relative;
        display: ${flag ? 'block' : 'none'};
        background: #F8FAFC;
        padding: 24px 80px 40px 80px;
        & .slick-track {
            display: inline-flex;
            align-items: flex-end;
            gap: 16px;
        }
    `,
    containerBox:css`
        width:100%;
        overflow-X:scroll;
        display:flex;
        gap:16px;
        align-items:flex-end;
        flex-wrap:nowrap;
        ::-webkit-scrollbar: {
            display: 'none' !important;
        }   
    `,
    rightArrow:css`
        box-shadow: 0px 0px 16px 0px #0000003D;
        width: 40px;
        height: 40px;
        padding: 8px, 8px, 8px, 8px;
        border-radius: 1000px;
        background-color:white;
        position:absolute;
        right:5%;
        top:50%;
        transform:translate(25%, -5%);
        z-index:1;
        cursor:pointer;
        display:flex;
        justify-content:center;
        align-items:center;
    `,
    leftArrow:css`
        box-shadow: 0px 0px 16px 0px #0000003D;
        width: 40px;
        height: 40px;
        padding: 8px, 8px, 8px, 8px;
        border-radius: 1000px;
        background-color:white;
        position:absolute;
        left:5%;
        top:50%;
        transform:translate(-50%, -5%) rotate(-180deg);
        z-index:6;
        cursor:pointer;
        display:flex;
        justify-content:center;
        align-items:center;
    `,
    skeletonContainer: css`
        display: flex;
        align-items: center;
        gap: 28px;
        background: #F8FAFC;
        padding: 40px 80px 40px 80px;
    `
}